/**
 * The person's sandbox, as the sandbox-first experience shows it (docs/SANDBOX_FIRST.md).
 *
 * The overview comes from vfs-service (`GET /api/v1/sandbox`): state, plan limits, usage and every service of the
 * caller's projects. It is served by the sandbox itself, so it works the same for a project built from Flow or from
 * an AI tool over MCP. Starting, stopping and the logs of one service go straight to the preview-runner through the
 * same universal preview route Canvas uses (`getContainerApiUrl`), which in per-person mode reaches the owner's pod.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { buildVfsSourcePayload } from '../utils/vfsClient';
import { getContainerApiUrl } from '../utils/previewSession';
import { buildSessionId } from '../utils/previewSessionId';
import { shareInflight } from '../utils/sharedRequest';
import { markSleptByChoice, resetSleptByChoice, sandboxSleptByChoice } from '../utils/sandboxSleep';
import { dockerOffOf, rememberDockerOff } from '../utils/runnablesModel';
import { cliState, rememberCliServices } from '../utils/cliServices';

export class SandboxError extends Error {
  constructor(message, status, code = null) {
    super(message);
    this.name = 'SandboxError';
    this.status = status;
    this.code = code;
  }
}

async function readError(response, fallback) {
  try {
    const body = await response.json();
    return new SandboxError(body?.message || body?.error || fallback, response.status, body?.code || null);
  } catch {
    return new SandboxError(fallback, response.status);
  }
}

const vfsUrl = (path) => `${config.vfsServiceUrl.replace(/\/$/, '')}${path}`;

/** The session id preview-runner and vfs-service share for a project's service: `<project>-services-app`. */
export function sessionIdFor(projectId, serviceName) {
  return buildSessionId(projectId, serviceName);
}

/**
 * One name per service: "app", "services/app", "./services/app/" are the same service, `services/app` (as vfs-service
 * names it); "" and "." are the project root; any other nested path stays as it is.
 */
export function canonicalServiceName(name) {
  const value = String(name ?? '').trim().replace(/\\/g, '/').replace(/^(\.\/)+/, '').replace(/\/{2,}/g, '/').replace(/^\/+|\/+$/g, '');
  if (!value || value === '.') { return '.'; }
  if (value.startsWith('services/') || value.includes('/')) { return value; }
  return `services/${value}`;
}

const failedBuild = (service) => service?.buildStatus === 'failed' || service?.buildStatus === 'error';
const rankOf = (service) => [
  isRunning(service) ? 0 : 1,
  failedBuild(service) ? 1 : 0,
  service.serviceName === service.runnerServiceName ? 0 : 1,
];

/**
 * The overview's services with one entry per project service (canonical `serviceName`; the runner's own name kept as
 * `runnerServiceName`): entries naming the same service ("app" next to "services/app") are merged, the running or
 * healthy one kept. The badge, the dashboard and the sandbox home all read this one list.
 */
export function normalizeServices(services) {
  const best = new Map();
  const order = [];
  for (const service of Array.isArray(services) ? services : []) {
    if (!service || typeof service !== 'object') { continue; }
    const name = canonicalServiceName(service.serviceName);
    const entry = { ...service, serviceName: name, runnerServiceName: service.serviceName };
    const key = `${service.projectId}\u0000${name}`;
    const current = best.get(key);
    if (!current) {
      best.set(key, entry);
      order.push(key);
      continue;
    }
    const [mine, theirs] = [rankOf(entry), rankOf(current)];
    const differs = mine.findIndex((value, index) => value !== theirs[index]);
    if (differs !== -1 && mine[differs] < theirs[differs]) { best.set(key, entry); }
  }
  return order.map((key) => best.get(key));
}

/** A service counts as running while it builds or its process is up. */
export function isRunning(service) {
  return Boolean(service) && (service.buildStatus === 'building' || service.containerStatus === 'running');
}

/**
 * True when a session's process crashed while the runner still reads "building": `crashed`, not running, with what it
 * printed last (`crashOutput`, which the runner clears whenever a new process starts, so a crash from before a restart
 * does not count).
 */
export function crashedWhileStarting(service) {
  return Boolean(service) && service.buildStatus === 'building' && service.crashed === true && service.containerStatus !== 'running'
    && Array.isArray(service.crashOutput) && service.crashOutput.some((line) => String(line || '').trim());
}

/**
 * One word for a service's state: building | running | crashed | failed | stopped, and `built` for a command-line tool
 * (utils/cliServices.js: built, never running nor crashed when it exits).
 */
export function serviceState(service) {
  if (!service) { return 'stopped'; }
  if (service.cli) { return cliState(service); }
  // Its process ended before it was ready (a `docker compose up` that could not pull an image): the runner may still
  // say "building", but the crash's own lines (`crashOutput`, cleared when a process starts) say it is over.
  if (crashedWhileStarting(service)) { return 'crashed'; }
  if (service.buildStatus === 'building') { return 'building'; }
  if (service.buildStatus === 'failed' || service.buildStatus === 'error') { return 'failed'; }
  if (service.crashed) { return 'crashed'; }
  return service.containerStatus === 'running' ? 'running' : 'stopped';
}

/**
 * `GET /api/v1/sandbox`: `{ mode, state, idleMinutes, plan: { name, limits, features, connections }, usage, services[],
 * connections, servicesUnavailable? }`.
 * `null` when vfs-service is not configured.
 */
export async function getSandbox() {
  if (!config.vfsServiceUrl) { return null; }
  // Home, the sidebar badge, Settings and the plan features each read it: one request while one is in flight.
  return shareInflight('sandbox-overview', async() => {
    const response = await getAuthorizedFetch()(vfsUrl('/api/v1/sandbox'), {
      headers: { Accept: 'application/json', ...buildAclContextHeaders() },
    });
    if (!response.ok) { throw await readError(response, `Sandbox overview failed (${response.status})`); }
    const overview = await response.json();
    return overview && Array.isArray(overview.services) ? { ...overview, services: normalizeServices(overview.services) } : overview;
  });
}

/**
 * `GET /api/v1/sandbox/state` (the sandbox gateway, vfs-service src/gateway/sandboxWake.js): how the person's sandbox
 * is doing, answered at once (it never wakes it): `{ state: running|waking|asleep|failed|none|unknown, phase?,
 * startedAt?, elapsedMs?, readyTimeoutMs?, error?, code? }`. `null` where vfs-service is not configured or the gateway
 * is too old to know it (404), or per-person sandboxes are off.
 */
export async function getSandboxState() {
  if (!config.vfsServiceUrl) { return null; }
  return shareInflight('sandbox-state', async() => {
    const response = await getAuthorizedFetch()(vfsUrl('/api/v1/sandbox/state'), {
      headers: { Accept: 'application/json', ...buildAclContextHeaders() },
      cache: 'no-store',
    });
    if (response.status === 404) { return null; }
    if (!response.ok) { throw await readError(response, `Sandbox state failed (${response.status})`); }
    return response.json();
  });
}

/**
 * `POST /api/v1/sandbox/wake`: starts the person's sandbox in the background and answers its state at once (the
 * Retry of "Waking your sandbox…"). Rejects with a SandboxError carrying the plan's refusal (429, `code`).
 */
export async function wakeSandbox() {
  if (!config.vfsServiceUrl) { return null; }
  resetSleptByChoice();
  const response = await getAuthorizedFetch()(vfsUrl('/api/v1/sandbox/wake'), {
    method: 'POST',
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (response.status === 404) { return null; }
  if (!response.ok) { throw await readError(response, `Waking the sandbox failed (${response.status})`); }
  return response.json();
}

/**
 * `GET /api/v1/projects/:id/services`: the state of ONE project's services, from wherever they run. Per person a
 * project's previews run in its owner's pod and the gateway sends this call there, so an editor or a viewer of
 * someone else's project sees the real state (the caller's own overview, `getSandbox`, only knows their own pod).
 * Resolves to `{ projectId, projectName, role, canRun, serviceNames[], services[], servicesUnavailable? }` (services normalized as the
 * overview's); `canRun` false for a viewer. `null` when vfs-service is not configured or too old to know the route.
 */
export async function getProjectServices(projectId) {
  if (!config.vfsServiceUrl || !projectId) { return null; }
  const response = await getAuthorizedFetch()(vfsUrl(`/api/v1/projects/${encodeURIComponent(projectId)}/services`), {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (response.status === 404) { return null; }
  if (!response.ok) { throw await readError(response, `Project services failed (${response.status})`); }
  const body = await response.json();
  // The command-line tools among them (utils/cliServices.js), for code that only has the project id.
  rememberCliServices(projectId, body?.cli);
  return {
    ...body,
    canRun: body?.canRun !== false,
    // The project's own services (`services/*`), started or not: what "start all" starts.
    serviceNames: Array.isArray(body?.serviceNames) ? [...new Set(body.serviceNames.map(canonicalServiceName))] : [],
    services: normalizeServices(body?.services).filter((service) => service.projectId === projectId),
  };
}

/**
 * `POST /api/v1/sandbox/sleep`: puts the person's sandbox to sleep now instead of waiting for the idle timeout.
 * Resolves to `{ state: 'asleep' }`; rejects with a SandboxError (409 in shared mode, 404 on an older vfs-service).
 */
export async function sleepSandbox() {
  if (!config.vfsServiceUrl) { throw new SandboxError('The sandbox is not configured', 404); }
  const response = await getAuthorizedFetch()(vfsUrl('/api/v1/sandbox/sleep'), {
    method: 'POST',
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (!response.ok) { throw await readError(response, `Putting the sandbox to sleep failed (${response.status})`); }
  markSleptByChoice();
  return response.json().catch(() => ({ state: 'asleep' }));
}

// The person put their sandbox to sleep in this tab (utils/sandboxSleep.js): background reads do not wake it.
export { sandboxSleptByChoice, resetSleptByChoice };

/**
 * `GET /api/v1/me/activity`: what happened in the person's sandbox across projects, including the sessions of their AI
 * tools (`{ sessions: [{ via, projectId, projectName, start, end, changes, lastMessage }], items }`). `null` when
 * vfs-service is not configured or too old to know it.
 */
export async function getMyActivity({ limit = 50 } = {}) {
  if (!config.vfsServiceUrl) { return null; }
  const response = await getAuthorizedFetch()(vfsUrl(`/api/v1/me/activity?limit=${encodeURIComponent(limit)}`), {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (response.status === 404) { return null; }
  if (!response.ok) { throw await readError(response, `Activity failed (${response.status})`); }
  return response.json();
}

/**
 * Whether the person's plan includes a feature (`plan.features`: chatOrgModels, chatOwnKey, playbooks): `true` or
 * `false` when the overview says so, `null` when it does not (an older vfs-service, or not loaded yet).
 */
export function planFeature(sandbox, key) {
  const value = sandbox?.plan?.features?.[key];
  return value === true || value === false ? value : null;
}

/**
 * The project tabs the person's plan hides (`plan.tabs.hidden`, set by the sandbox admin; utils/projectTabsModel.js
 * ids, never Home): a list, empty when the overview says none or nothing (an older vfs-service).
 */
export function planHiddenTabs(sandbox) {
  const hidden = sandbox?.plan?.tabs?.hidden;
  return Array.isArray(hidden) ? hidden.filter((id) => typeof id === 'string' && id !== 'dashboard') : [];
}

async function runnerPost(endpoint, body) {
  const response = await getAuthorizedFetch()(getContainerApiUrl(null, endpoint), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) { throw await readError(response, `Preview request failed (${response.status})`); }
  // A redirect followed to a page (a wrong route answers with Flow's own HTML) is not the runner saying yes.
  if (response.redirected || !String(response.headers?.get?.('content-type') || 'application/json').includes('json')) {
    throw new SandboxError('The preview service did not answer. Try again in a moment.', response.status);
  }
  return response.json().catch(() => ({}));
}

/** The runner's view of one service: `{ buildStatus, buildPhase, buildProgress, buildError, previewPort, crashed }`, or null when it has none. */
export async function getServiceStatus(sessionId) {
  const response = await getAuthorizedFetch()(getContainerApiUrl(null, `/api/preview/${encodeURIComponent(sessionId)}/status`), {
    headers: { Accept: 'application/json' },
  });
  if (response.status === 404) { return null; }
  if (!response.ok || response.redirected) { throw new SandboxError(`Preview status failed (${response.status})`, response.status); }
  const body = await response.json().catch(() => ({}));
  return body.session || null;
}

/** Stops a service and keeps it, so Start brings it back without a rebuild. */
// Stop all (RunAllActions) sends it with `{ projectId }`: what waits for the project's services (Quality's "Start and
// test") stops waiting.
export const PROJECT_STOPPED_EVENT = 'flow:project-stopped';

/** Tells the page that the project's services were stopped on purpose. */
export function notifyProjectStopped(projectId) {
  try { window.dispatchEvent(new CustomEvent(PROJECT_STOPPED_EVENT, { detail: { projectId } })); } catch { /* no window */ }
  // The sandbox's own "Start and test" wait, and a "Create lockfiles" that starts the services, end too, even with the
  // Quality tab closed (a viewer's 403, or an older sandbox's 404, is ignored).
  import('./QualityService').then((quality) => {
    quality.cancelStartAndTest(projectId).catch(() => {});
    quality.cancelLockfiles(projectId).catch(() => {});
  }).catch(() => {});
}

export function stopService(sessionId) {
  return runnerPost(`/api/preview/${encodeURIComponent(sessionId)}/stop`);
}

/** Removes a service's preview from the runner (a failed or stale entry); Start builds it again from vfs-service. */
export async function removeService(sessionId) {
  const response = await getAuthorizedFetch()(getContainerApiUrl(null, `/api/preview/${encodeURIComponent(sessionId)}`), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
  });
  // Already gone is what was asked.
  if (!response.ok && response.status !== 404) { throw await readError(response, `Removing the preview failed (${response.status})`); }
  return true;
}

/** Restarts a running service, or starts a stopped one. Over the plan's limit it rejects with code `limit_running_services`. */
export function restartService(sessionId) {
  return runnerPost(`/api/preview/${encodeURIComponent(sessionId)}/restart`);
}

/**
 * Starts a service that has no session yet (never built, or reaped while idle): builds it from vfs-service. A
 * service that still has a session is started with `restartService`. `runnable` (`{ kind, file }`, utils/runnablesModel):
 * a Dockerfile or compose file pinned in the service, run as a session of its own (`<service>--<kind>-<slug>`).
 */
export function startService(projectId, serviceName, { runnable = null } = {}) {
  return runnerPost('/api/render', {
    serviceName,
    projectId,
    ...buildVfsSourcePayload(projectId, serviceName),
    // A compose file's saved preview entry point goes with it (`{ service, port }`; the runner rejects other fields).
    ...(runnable ? { runnable: { kind: runnable.kind, path: runnable.file, ...(runnable.entry ? { entry: { service: runnable.entry.service, port: runnable.entry.port } } : {}) } } : {}),
  });
}

/**
 * `GET /api/v1/projects/:id/runnables`: the Dockerfiles and compose files found in each service (vfs-service
 * src/preview/runnables.js): `{ docker, services: { 'services/<name>': { runnables: [{ path, file, kind, own?,
 * services? }], truncated } }, outside: [{ path, services }] }` (`outside`: compose files outside `services/`). `docker` false: the plan has no "Docker in the sandbox". Null on an older vfs-service.
 * `dockerOff`: why it is off, `'cluster'` (this Flow has no Docker support) or `'plan'`; null when on or not said.
 */
export async function getRunnables(projectId) {
  if (!config.vfsServiceUrl || !projectId) { return null; }
  const response = await getAuthorizedFetch()(vfsUrl(`/api/v1/projects/${encodeURIComponent(projectId)}/runnables`), {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (response.status === 404) { return null; }
  if (!response.ok) { throw await readError(response, `Reading the Dockerfiles and compose files failed (${response.status})`); }
  const body = await response.json().catch(() => ({}));
  const dockerOff = body?.docker === false ? dockerOffOf(body?.dockerOff) : null;
  rememberDockerOff(projectId, dockerOff);
  return {
    docker: typeof body?.docker === 'boolean' ? body.docker : null,
    dockerOff,
    services: body?.services || {},
    outside: Array.isArray(body?.outside) ? body.outside : [],
  };
}

/**
 * `POST /api/v1/projects/:id/runnables/move-outside` (vfs-service src/preview/composeOutside.js): a Docker Compose app
 * found outside `services/` (`file`, project-relative) moved into `services/<name>/` with the folders only it uses, its
 * relative paths rewritten. Without `apply` it only answers the plan: `{ file, name, to, moves: [{ from, to }],
 * rewrites: [{ service, kind, from, to }], serviceJson }`; with it, the same, applied as one commit.
 */
export async function moveComposeOutside(projectId, { file, name, apply = false }) {
  const response = await getAuthorizedFetch()(vfsUrl(`/api/v1/projects/${encodeURIComponent(projectId)}/runnables/move-outside`), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...buildAclContextHeaders() },
    body: JSON.stringify({ file, name, apply }),
  });
  if (!response.ok) { throw await readError(response, `Moving ${file} failed (${response.status})`); }
  return response.json();
}

const composeUrl = (sessionId, rest = '') => `/api/preview/${encodeURIComponent(sessionId)}/compose/services${rest}`;

/**
 * The compose services of a session that runs a compose app (preview-runner `compose_routes.rs`): `{ running,
 * services: [{ name, state, status, ports }] }`, or null when the runner has no such session (not started yet).
 */
export async function getComposeServices(sessionId) {
  const response = await getAuthorizedFetch()(getContainerApiUrl(null, composeUrl(sessionId)), { headers: { Accept: 'application/json' } });
  if (response.status === 404 || response.status === 409) { return null; }
  if (!response.ok || response.redirected) { throw await readError(response, `Compose services failed (${response.status})`); }
  const body = await response.json().catch(() => ({}));
  // `entry`: the preview entry point's candidates and choice (null when the session is not a compose app).
  return { running: body.running === true, services: Array.isArray(body.services) ? body.services : [], entry: body.entry || null };
}

/** Starts (`up -d`) or stops one compose service of a running compose app. */
export function composeServiceAction(sessionId, service, action) {
  return runnerPost(composeUrl(sessionId, `/${encodeURIComponent(service)}/${action === 'stop' ? 'stop' : 'start'}`));
}

/** The last log lines of one compose service. */
export async function getComposeLogs(sessionId, service, { tail = 200 } = {}) {
  const response = await getAuthorizedFetch()(getContainerApiUrl(null, composeUrl(sessionId, `/${encodeURIComponent(service)}/logs?tail=${tail}`)), {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok || response.redirected) { throw await readError(response, `Logs failed (${response.status})`); }
  const body = await response.json().catch(() => ({}));
  return Array.isArray(body.lines) ? body.lines : [];
}

/** The service's log stream (server-sent events: `initial`, then `build` | `stdout` | `stderr` lines). */
export function logsStreamUrl(sessionId) {
  return getContainerApiUrl(null, `/api/preview/${encodeURIComponent(sessionId)}/logs/stream`);
}

/** Human size: 1536 -> "1.5 KB". */
export function formatBytes(bytes) {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) { return '—'; }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * CPU as vCPU (cores) from a percent of one core, as vfs-service and preview-runner report it (`top`-like: 250 = 2.5
 * cores): "0.12 vCPU", "2.5 vCPU"; "—" when unknown. The plan's CPU limit is in the same unit.
 */
export function formatCores(cpuPercent) {
  if (cpuPercent === null || cpuPercent === undefined || Number.isNaN(Number(cpuPercent))) { return '—'; }
  const cores = Number(cpuPercent) / 100;
  return `${cores < 1 ? cores.toFixed(2) : cores.toFixed(1)} vCPU`;
}

/** "3 h", "41 min", "just now" since an ISO time. */
export function formatUptime(since, now = Date.now()) {
  if (!since) { return '—'; }
  const minutes = Math.floor((now - new Date(since).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) { return 'just now'; }
  if (minutes < 60) { return `${minutes} min`; }
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${hours} h` : `${Math.floor(hours / 24)} days`;
}
